import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Pencil, Plus, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { EtainablSpinner } from '@/components/etainabl-spinner';
import { useDeleteKbCollection, useKbCollections, useSaveKbCollection } from '@/queries/kb';
import { extractErrorMessage } from '@/queries/ticketMutations';
import type { KbCollection } from '@/types/kb';
import { KbRestrictedBadge } from './KbRestrictedBadge';
import { KbVisibilityFields, type KbVisibilityCompany } from './KbVisibilityFields';

export function KbCollectionsAdminView() {
  const { data, isLoading } = useKbCollections();
  const del = useDeleteKbCollection();
  // undefined = closed, null = creating, otherwise the collection being edited.
  const [editing, setEditing] = useState<KbCollection | null | undefined>(undefined);

  return (
    <div className="container mx-auto max-w-4xl p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Knowledgebase collections</h1>
        <Button size="sm" onClick={() => setEditing(null)}>
          <Plus className="mr-1.5 h-4 w-4" />
          New collection
        </Button>
      </div>
      <p className="text-sm text-muted-foreground">
        Readers see a collection, and the articles in it, only when its company and feature restrictions allow. The default collection holds the core
        guides and is open to everyone.
      </p>

      {isLoading && (
        <div className="py-8 flex justify-center">
          <EtainablSpinner size="sm" />
        </div>
      )}

      <ul className="divide-y rounded border bg-background">
        {data?.map(c => (
          <li key={c._id} className="flex items-center gap-3 px-4 py-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <Link to={`/kb/c/${c.slug}` as never} className="font-medium hover:underline">
                  {c.name}
                </Link>
                {c.isDefault && <span className="text-xs text-muted-foreground">Default</span>}
                <KbRestrictedBadge article={c} />
              </div>
              {c.description && <div className="text-sm text-muted-foreground mt-0.5">{c.description}</div>}
              <div className="flex flex-wrap gap-x-3 text-xs text-muted-foreground mt-0.5">
                <span>Order {c.order}</span>
                <span>
                  {c.articleCount} article{c.articleCount === 1 ? '' : 's'}
                </span>
              </div>
            </div>
            <Button size="sm" variant="ghost" aria-label={`Edit ${c.name}`} onClick={() => setEditing(c)}>
              <Pencil className="h-4 w-4" />
            </Button>
            {!c.isDefault && (
              <Button
                size="sm"
                variant="ghost"
                aria-label={`Delete ${c.name}`}
                onClick={async () => {
                  if (c.articleCount > 0) {
                    toast.error(`Move or delete the ${c.articleCount} article${c.articleCount === 1 ? '' : 's'} in "${c.name}" first.`);
                    return;
                  }
                  if (!confirm(`Delete "${c.name}"?`)) return;
                  try {
                    await del.mutateAsync(c._id);
                    toast.success('Collection deleted');
                  } catch (err) {
                    toast.error(extractErrorMessage(err, 'Could not delete'));
                  }
                }}
              >
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            )}
          </li>
        ))}
      </ul>

      {editing !== undefined && <KbCollectionDialog collection={editing} onClose={() => setEditing(undefined)} />}
    </div>
  );
}

function KbCollectionDialog({ collection, onClose }: { collection: KbCollection | null; onClose: () => void }) {
  const save = useSaveKbCollection(collection?._id);
  const [name, setName] = useState(collection?.name ?? '');
  const [description, setDescription] = useState(collection?.description ?? '');
  const [order, setOrder] = useState(collection ? String(collection.order) : '');
  const [companies, setCompanies] = useState<KbVisibilityCompany[]>(() =>
    (collection?.companyIds ?? []).map(id => ({ _id: id, name: collection?.companies?.find(c => c._id === id)?.name }))
  );
  const [featureFlags, setFeatureFlags] = useState<string[]>(collection?.featureFlags ?? []);
  const isDefault = Boolean(collection?.isDefault);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Name is required');
      return;
    }
    const parsedOrder = order.trim() === '' ? undefined : Number(order);
    if (parsedOrder !== undefined && !Number.isInteger(parsedOrder)) {
      toast.error('Order must be a whole number');
      return;
    }
    try {
      await save.mutateAsync({
        name: name.trim(),
        description: description.trim(),
        ...(parsedOrder !== undefined ? { order: parsedOrder } : {}),
        // The API refuses restrictions on the default, so they are never sent for it.
        ...(isDefault ? {} : { companyIds: companies.map(c => c._id), featureFlags })
      });
      toast.success(collection ? 'Collection saved' : 'Collection created');
      onClose();
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Could not save'));
    }
  }

  return (
    <Dialog open onOpenChange={open => !open && onClose()}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>{collection ? 'Edit collection' : 'New collection'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-2">
            <Label htmlFor="kb-collection-name">Name</Label>
            <Input
              id="kb-collection-name"
              value={name}
              maxLength={100}
              onChange={e => setName(e.target.value)}
              placeholder="Fidelity Energy sales training"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="kb-collection-description">Description (optional)</Label>
            <Textarea id="kb-collection-description" rows={2} maxLength={500} value={description} onChange={e => setDescription(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="kb-collection-order">Order</Label>
            <Input
              id="kb-collection-order"
              type="number"
              step={1}
              className="max-w-32"
              value={order}
              onChange={e => setOrder(e.target.value)}
              placeholder="Last"
            />
            <p className="text-etn-micro text-etn-text-soft">Lower numbers come first on the Knowledgebase home.</p>
          </div>

          {isDefault ? (
            <p className="text-sm text-muted-foreground">
              This is the default collection. It is open to everyone and cannot be restricted or deleted.
            </p>
          ) : (
            <KbVisibilityFields
              companies={companies}
              onCompaniesChange={setCompanies}
              featureFlags={featureFlags}
              onFeatureFlagsChange={setFeatureFlags}
            />
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={save.isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={save.isPending}>
              {save.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {collection ? 'Save' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
